import { useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { render, pretty } from "@react-email/render";
import EPReferralTemplate from "./EPReferralTemplate";

type Inputs = {
  client_name: string;
  client_dob: Date;
  client_address: string;
  client_gender: string;
  client_phone: string;
  client_email: string;
  medicare_num: string;
  medicare_irn: string;
  medicare_expiry: string;
  private_cover_fund: string;
  private_cover_num: string;
  referral_reason: string;
  
  client_emergency_contact: string;
  client_emergency_phone: string;

  referrer_id: string;
  referrer_profession: string;
  referrer_org: string;
  ref_provider_num: string;
  ref_provider_phone: string;
  ref_provider_email: string;
  ref_previous_contact: string;

  goal_one: string;
  goal_two: string;
  goal_three: string;
  goal_four: string;
  funding_pathway: string;
  funding_pathway_other: string;

  // NDIS
  ndis_num: string;
  plan_start: Date;
  plan_end: Date;
  primary_disability: string;
  sc_name: string;
  sc_phone: string;
  sc_email: string;
  fund_type: string;
  fund_plan_email: string;

  // TAC
  tac_claim_num: string;
  tac_case_manager_name: string;
  tac_case_manager_phone: string;
  tac_case_manager_email: string;
  tac_injury_date: Date;
  tac_occupation: string;
  tac_current_capacity: string;

  // WORKCOVER
  wc_claim_num: string;
  wc_case_manager_name: string;
  wc_case_manager_phone: string;
  wc_case_manager_email: string;
  wc_injury_date: Date;
  wc_insurer: string;
  wc_employer: string;
  wc_current_capacity: string;

  //DVA
  dva_card_num: string;
  dva_card_type: string;
  dva_accepted_condition: string;

  file_upload: File;
}

const EPReferralForm = () => {
  const [funding, setFunding] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [base64String, setBase64String] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [submitTitle, setSubmitTitle] = useState<string>()
  const [submitMessage, setSubmitMessage] = useState<string>()

  const {
    register,
    // formState: { errors },
    handleSubmit,
  } = useForm<Inputs>();

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    setLoading(false);
  };

  const handleFundingSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFunding(e.target.value)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (!files) {
      setFile(null)
      setBase64String('')
      return
    }

    setFile(files[0])
    convertFileToBase64(files[0])
  } 

  const convertFileToBase64 = (file: any) => {
    const reader = new FileReader()

    // On successful read, extract Base64 string
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1]
      setBase64String(base64)
    }

    // Handle errors
    reader.onerror = () => {
      console.log('FileReader error: ' + reader.error)
    }

    // Start reading file as a data URL (Base64)
    reader.readAsDataURL(file)
  }

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setLoading(true)

    const emailHtml = await pretty(
      await render(
        <EPReferralTemplate
          data={ data }
        />,
      ),
    )

    let emailBody
    if (file != null) {
      emailBody = JSON.stringify({
        "to": import.meta.env.VITE_EP_EMAIL,
        "subject": "EP Referral: " + data.client_name,
        "body": emailHtml,
        "from": {
          "name": "Provido",
          "email": "noreply@provido.com.au",
        },
        "attachments": [
          {
            "filename": file.name,
            "content": base64String,
            "contentType": file.type
          }
        ],
      })
    } else {
      emailBody = JSON.stringify({
        "to": import.meta.env.VITE_EP_EMAIL,
        "subject": "EP Referral: " + data.client_name,
        "body": emailHtml,
        "from": {
          "name": "Provido",
          "email": "noreply@provido.com.au",
        },
      })
    }

    await fetch("https://next-api.useplunk.com/v1/send", {
      "method": "POST",
      "headers": {
        "Content-Type": "application/json",
        "Authorization": import.meta.env.VITE_EMAIL_API,
      },
      "body": emailBody
    })
    .then(() => {
      setSubmitTitle("Thank You");
      setSubmitMessage(
        "Thank you for sending your referral to Provido. We'll get back to you as soon as possible.",
      );
      openModal();
    })
    .catch((error) => {
      console.log(error);
      setSubmitTitle("Something Went Wrong");
      setSubmitMessage(
        "Looks like something went wrong. Please try again or email us via our contact page. Sorry for any inconvenience.",
      );
      openModal();
    });
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mx-auto mt-8 flex max-w-xl flex-col gap-4 px-4"
      >
        {/* Client Information */}
        <fieldset>
          <legend>Client Information</legend>
          <div>
            <label htmlFor="client_name">
              Name:
            </label>
            <input
              type="text"
              {...register("client_name", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="client_dob">Date of birth:</label>
            <input
              type="date"
              {...register("client_dob", { required: false })}
              className="form-field"
            />
          </div>
          <div>
            <label htmlFor="client_address">
              Address:
            </label>
            <input
              type="text"
              {...register("client_address", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="client_gender">
              Gender:
            </label>
            <div className="pt-2">
              <input
                type="radio"
                {...register("client_gender", { required: false })}
                value="Male"
                name="client_gender"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Male</span>
              <input
                type="radio"
                {...register("client_gender", { required: false })}
                value="Female"
                name="client_gender"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Female</span>
              <input
                type="radio"
                {...register("client_gender", { required: false })}
                value="Prefer not to say"
                name="client_gender"
                className="mx-2 accent-blue"
              />{" "}
              <span className="font-body">Prefer not to say</span>
            </div>
          </div>
          <div>
            <label htmlFor="client_phone">
              Phone:
            </label>
            <input
              type="text"
              {...register("client_phone", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="client_email">
              Email:
            </label>
            <input
              type="email"
              {...register("client_email", { required: false })}
              className="form-field"
            ></input>
          </div>
          <fieldset>
            <legend>Medicare Information</legend>
            <div>
              <label htmlFor="medicare_num">
                Card number:
              </label>
              <input
                type="text"
                {...register("medicare_num", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="medicare_irn">
                Individual reference number:
              </label>
              <input
                type="text"
                {...register("medicare_irn", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="medicare_expiry">
                Expiry date:
              </label>
              <input
                type="text"
                {...register("medicare_expiry", { required: false })}
                className="form-field"
              ></input>
            </div>
          </fieldset> {/* Medicare details */}
          <fieldset>
            <legend>Private Health Cover Information</legend>
            <div>
              <label htmlFor="private_cover_fund">
                Private cover name:
              </label>
              <input
                type="text"
                {...register("private_cover_fund", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="private_cover_num">
                Member number:
              </label>
              <input
                type="text"
                {...register("private_cover_num", { required: false })}
                className="form-field"
              ></input>
            </div>
          </fieldset> {/* Private health info */}
          <div>
            <label htmlFor="referral_reason">
              Reason for referral / diagnosis / condition:{" "}
            </label>
            <textarea
              {...register("referral_reason", { required: false })}
              className="form-field"
            />
          </div>
        </fieldset> {/* Client Info */}

        {/* Emergency Contact */}
        <fieldset>
          <legend>Emergency Contact</legend>
          <div>
            <label htmlFor="client_emergency_contact">
              Name:
            </label>
            <input
              type="text"
              {...register("client_emergency_contact", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="client_emergency_phone">
              Phone:
            </label>
            <input
              type="text"
              {...register("client_emergency_phone", { required: false })}
              className="form-field"
            ></input>
          </div>
        </fieldset> {/* Emergency contact */}

        {/* Referrer Information */}
        <fieldset>
          <legend>Referrer Information</legend>
          <div>
            <label htmlFor="referrer_id">
              Are you a:
            </label>
            <div className="pt-2">
              <input
                type="radio"
                {...register("referrer_id", { required: false })}
                value="Participant / Client"
                name="referrer_id"
                className="mx-2 mb-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Participant / Client</span><br />
              <input
                type="radio"
                {...register("referrer_id", { required: false })}
                value="Referrer / Nominated Representative"
                name="referrer_id"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Referrer / Nominated Representative</span>
            </div>
          </div>
          <div>
            <label htmlFor="referrer_profession">
              Referrer profession (if applicable):
            </label>
            <div className="pt-2">
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="GP"
                name="referrer_profession"
                className="mx-2 mb-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">GP</span><br />
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="Physiotherapist"
                name="referrer_profession"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Physiotherapist</span><br />
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="Occupational Therapist"
                name="referrer_profession"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Occupational Therapist</span><br />
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="Allied Health"
                name="referrer_profession"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Allied Health</span><br />
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="Specialist"
                name="referrer_profession"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Specialist</span><br />
              <input
                type="radio"
                {...register("referrer_profession", { required: false })}
                value="Mental Health"
                name="referrer_profession"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Mental Health</span>
            </div>
          </div>
          <fieldset>
            <legend>Referrer Practice / Organisation Information</legend>
            <div>
              <label htmlFor="referrer_org">
                Name:
              </label>
              <input
                type="text"
                {...register("referrer_org", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="ref_provider_num">
                Provider number:
              </label>
              <input
                type="text"
                {...register("ref_provider_num", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="ref_provider_phone">
                Phone:
              </label>
              <input
                type="text"
                {...register("ref_provider_phone", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="ref_provider_email">
                Email:
              </label>
              <input
                type="email"
                {...register("ref_provider_email", { required: false })}
                className="form-field"
              ></input>
            </div>
          </fieldset> {/* Referrer Org info */}
          <div>
            <label htmlFor="ref_previous_contact">
              Have you used our services before?:
            </label>
            <div className="pt-2">
              <input
                type="radio"
                {...register("ref_previous_contact", { required: false })}
                value="Yes"
                name="ref_previous_contact"
                className="mx-2 mb-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Yes</span>
              <input
                type="radio"
                {...register("ref_previous_contact", { required: false })}
                value="No"
                name="ref_previous_contact"
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">No</span>
            </div>
          </div>
        </fieldset> {/* Referrer info */}

        {/* Goals */}
        <fieldset>
          <legend>What goals are you hoping to achieve?</legend>
          <div>
            <label htmlFor="goal_one">
              One:
            </label>
            <input
              type="text"
              {...register("goal_one", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="goal_two">
              Two:
            </label>
            <input
              type="text"
              {...register("goal_two", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="goal_three">
              Three:
            </label>
            <input
              type="text"
              {...register("goal_three", { required: false })}
              className="form-field"
            ></input>
          </div>
          <div>
            <label htmlFor="goal_four">
              Four:
            </label>
            <input
              type="text"
              {...register("goal_four", { required: false })}
              className="form-field"
            ></input>
          </div>
        </fieldset> {/* Goals */}

        {/* Funding Pathways */}
        <fieldset>
          <legend>Funding</legend>
          <div>
            <label htmlFor="funding_pathway">
              Please select the applicable funding pathway:
            </label>
            <div className="pt-2">
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="Private / Self-funded"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 mb-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Private / Self-funded</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="Medicare / Chronic Disease Management Plan"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Medicare / Chronic Disease Management Plan</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="NDIS"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">NDIS</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="TAC"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">TAC</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="WorkCover"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">WorkCover</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="DVA"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">DVA</span><br />
              <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="Other"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Other: 
                <input
                  type="text"
                  {...register("funding_pathway_other", { required: false })}
                  className="form-field"
                  placeholder="If other, please let us know how you will be paying"
                ></input>
              </span>
            </div>
          </div>
          {funding=='NDIS' && <fieldset>
            <legend> NDIS ONLY</legend>
            <div>
              <label htmlFor="ndis_num">
                NDIS Number:
              </label>
              <input
                type="text"
                {...register("ndis_num", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="plan_start">
                Plan start date:
              </label>
              <input
                type="date"
                {...register("plan_start", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="plan_end">
                Plan end date:
              </label>
              <input
                type="date"
                {...register("plan_end", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="primary_disability">
                Primary disability:
              </label>
              <input
                type="text"
                {...register("primary_disability", { required: false })}
                className="form-field"
              ></input>
            </div>

            <fieldset>
              <legend>Support Coordinator</legend>
              <div>
                <label htmlFor="sc_name">
                  Name:
                </label>
                <input
                  type="text"
                  {...register("sc_name", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="sc_phone">
                  Phone:
                </label>
                <input
                  type="text"
                  {...register("sc_phone", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="sc_email">
                  Email:
                </label>
                <input
                  type="email"
                  {...register("sc_email", { required: false })}
                  className="form-field"
                ></input>
              </div>
            </fieldset> {/* SC Details */}

            <div>
              <label htmlFor="fund_type">
                How are funds claimed?
              </label>
              <div className="pt-2">
                <input
                  type="radio"
                  {...register("fund_type", { required: false })}
                  value="Self Managed"
                  name="fund_type"
                  className="mx-2 mb-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Self Managed</span><br />
                <input
                  type="radio"
                  {...register("fund_type", { required: false })}
                  value="Agency Managed"
                  name="fund_type"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Agency Managed</span><br />
                <input
                type="radio"
                {...register("funding_pathway", { required: false })}
                value="Plan Managed"
                name="funding_pathway"
                onChange={handleFundingSelect}
                className="mx-2 accent-blue"
              />{" "}
              <span className="mr-8 font-body">Plan Managed: 
                <input
                  type="text"
                  {...register("fund_plan_email", { required: false })}
                  className="form-field"
                  placeholder="If plan managed, email for invoices"
                ></input></span>
              </div>
            </div>
          </fieldset>} {/* NDIS only */}

          {funding=='TAC' && <fieldset>
            <legend>TAC ONLY</legend>
            <div>
              <label htmlFor="tac_claim_num">
                Claim number:
              </label>
              <input
                type="text"
                {...register("tac_claim_num", { required: false })}
                className="form-field"
              ></input>
            </div>

            <fieldset>
              <legend>Case Manager</legend>
              <div>
                <label htmlFor="tac_case_manager_name">
                  Name:
                </label>
                <input
                  type="text"
                  {...register("tac_case_manager_name", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="tac_case_manager_phone">
                  Phone:
                </label>
                <input
                  type="text"
                  {...register("tac_case_manager_phone", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="tac_case_manager_email">
                  Email:
                </label>
                <input
                  type="email"
                  {...register("tac_case_manager_email", { required: false })}
                  className="form-field"
                ></input>
              </div>
            </fieldset> {/* Case manager details */}
            <div>
              <label htmlFor="tac_injury_date">
                Date of injury:
              </label>
              <input
                type="date"
                {...register("tac_injury_date", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="tac_occupation">
                Occupation:
              </label>
              <input
                type="text"
                {...register("tac_occupation", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="tac_current_capacity">
                Current work capacity
              </label>
              <div className="pt-2">
                <input
                  type="radio"
                  {...register("tac_current_capacity", { required: false })}
                  value="Not Working"
                  name="tac_current_capacity"
                  className="mx-2 mb-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Not Working</span><br />
                <input
                  type="radio"
                  {...register("tac_current_capacity", { required: false })}
                  value="Full time - full capacity"
                  name="tac_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Full time - full capacity</span><br />
                <input
                  type="radio"
                  {...register("tac_current_capacity", { required: false })}
                  value="Full time - modified capacity"
                  name="tac_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Full time - modified capacity</span><br />
                <input
                  type="radio"
                  {...register("tac_current_capacity", { required: false })}
                  value="Part time - full capacity"
                  name="tac_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Part time - full capacity</span><br />
                <input
                  type="radio"
                  {...register("tac_current_capacity", { required: false })}
                  value="Part time - modified capacity"
                  name="tac_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Part time - modified capacity</span>
              </div>
            </div>
            <strong>Please upload your approval for EP services below.</strong>
          </fieldset>} {/* TAC only */}

          {funding=='WorkCover' && <fieldset>
            <legend>WORKCOVER ONLY</legend>
            <div>
              <label htmlFor="wc_claim_num">
                Claim number:
              </label>
              <input
                type="text"
                {...register("wc_claim_num", { required: false })}
                className="form-field"
              ></input>
            </div>
            
            <fieldset>
              <legend>Case Manager</legend>
              <div>
                <label htmlFor="wc_case_manager_name">
                  Name:
                </label>
                <input
                  type="text"
                  {...register("wc_case_manager_name", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="wc_case_manager_phone">
                  Phone:
                </label>
                <input
                  type="text"
                  {...register("wc_case_manager_phone", { required: false })}
                  className="form-field"
                ></input>
              </div>
              <div>
                <label htmlFor="wc_case_manager_email">
                  Email:
                </label>
                <input
                  type="email"
                  {...register("wc_case_manager_email", { required: false })}
                  className="form-field"
                ></input>
              </div>
            </fieldset> {/* Case manager details */}
            <div>
              <label htmlFor="wc_injury_date">
                Date of injury:
              </label>
              <input
                type="date"
                {...register("tac_injury_date", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="wc_insurer">
                WorkCover insurer:
              </label>
              <input
                type="text"
                {...register("wc_insurer", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="wc_employer">
                Employer:
              </label>
              <input
                type="text"
                {...register("wc_employer", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="wc_current_capacity">
                Current work capacity
              </label>
              <div className="pt-2">
                <input
                  type="radio"
                  {...register("wc_current_capacity", { required: false })}
                  value="Not Working"
                  name="wc_current_capacity"
                  className="mx-2 mb-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Not Working</span><br />
                <input
                  type="radio"
                  {...register("wc_current_capacity", { required: false })}
                  value="Full time - full capacity"
                  name="wc_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Full time - full capacity</span><br />
                <input
                  type="radio"
                  {...register("wc_current_capacity", { required: false })}
                  value="Full time - modified capacity"
                  name="wc_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Full time - modified capacity</span><br />
                <input
                  type="radio"
                  {...register("wc_current_capacity", { required: false })}
                  value="Part time - full capacity"
                  name="wc_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Part time - full capacity</span><br />
                <input
                  type="radio"
                  {...register("wc_current_capacity", { required: false })}
                  value="Part time - modified capacity"
                  name="wc_current_capacity"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Part time - modified capacity</span>
              </div>
            </div>
            <strong>Please upload your approval for EP services below.</strong>
          </fieldset>} {/* WorkCover only */}
          {funding=='DVA' && <fieldset>
            <legend>DVA ONLY</legend>
            <div>
              <label htmlFor="dva_card_num">
                Card number:
              </label>
              <input
                type="text"
                {...register("dva_card_num", { required: false })}
                className="form-field"
              ></input>
            </div>
            <div>
              <label htmlFor="dva_card_type">
                DVA card type
              </label>
              <div className="pt-2">
                <input
                  type="radio"
                  {...register("dva_card_type", { required: false })}
                  value="Gold"
                  name="dva_card_type"
                  className="mx-2 mb-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">Gold <strong>(please upload GP referral below)</strong></span><br />
                <input
                  type="radio"
                  {...register("dva_card_type", { required: false })}
                  value="White"
                  name="dva_card_type"
                  className="mx-2 accent-blue"
                />{" "}
                <span className="mr-8 font-body">White <strong>(please upload injury-specific referral below)</strong></span>
              </div>
            </div>
            <div>
              <label htmlFor="dva_accepted_condition">
                Accepted / relevant condition:
              </label>
              <input
                type="text"
                {...register("dva_accepted_condition", { required: false })}
                className="form-field"
              ></input>
            </div>
          </fieldset>} {/* DVA only */}
        </fieldset> {/* Funding */}
        {(funding=='TAC' || funding=='WorkCover' || funding=='DVA') && <fieldset>
          <legend>File Upload</legend>
          <div className='flex items-center justify-center w-full'>
            <label htmlFor='file_upload'>
              <input id='file_upload' type='file' accept='.pdf, .docx, .doc, .odt' className='cursor-pointer form-field-file file:bg-blue file:p-2 file:mr-2 file:text-white file:cursor-pointer' onChange={ handleFileInput } />
              <div className='mt-1 text-gray-500'>PDF, DOC, DOCX up to 5MB.</div>
            </label>
          </div>
        </fieldset>} {/* File upload */}
        <div className="flex flex-row justify-between">
          <button
            type="submit"
            disabled={loading}
            className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-darkblue disabled:bg-blue/10 hover:disabled:bg-blue/10"
          >
            Submit
          </button>
          <button
            type="reset"
            className="hover:bg-grey w-fit rounded-xl bg-gray-200 px-4 py-2 font-semibold hover:cursor-pointer hover:bg-gray-400 hover:text-white"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Submission Dialog Box */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed top-0 left-0 z-49 h-screen w-screen bg-lightblue/20"></div>
        <div className="fixed inset-0 z-50 flex w-screen items-center justify-center">
          <DialogPanel className="max-w-lg space-y-4 rounded-xl border-2 border-lightblue bg-white p-6">
            <DialogTitle className="pb-2 font-display text-2xl text-blue">
              {submitTitle}
            </DialogTitle>
            <p className="text-grey font-body">{submitMessage}</p>
            <div className="flex justify-end">
              <button
                className="w-fit rounded-xl bg-blue px-4 py-2 font-semibold text-white transition hover:cursor-pointer hover:bg-darkblue"
                onClick={() => closeModal()}
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  )
}

export default EPReferralForm